<script setup lang="ts">
/**
 * Compact SVG sparkline showing per-month bucket counts as vertical bars.
 *
 * Renders an optional x-axis (strided labels), a y-axis gutter (max + 0
 * ticks), and a cursor-projected hover tooltip. The tooltip is implemented
 * via a single `mousemove` listener on the wrapper div rather than native
 * SVG `<title>` elements — Safari skips `<title>` on transparent fills and
 * browsers impose a ~1 s hover delay that reads as broken.
 *
 * Used in `ClientCard` to visualise a client's monthly visit history.
 */
import { computed, ref } from 'vue'

const props = withDefaults(
  defineProps<{
    /**
     * Per-month bucket counts in chronological order: index 0 is the
     * oldest, last index is the most recent. The component renders a thin
     * vertical bar for each bucket, scaled to the maximum value in the
     * series. Pass an empty array (or a series of zeros) to render the
     * empty grid.
     */
    buckets: number[]
    /**
     * Optional per-bucket month labels (same length / index convention as
     * `buckets`). Only every `labelStride`-th label is drawn beneath the
     * axis; defaults to a stride of `6` so the typical 24-month sparkline
     * shows four labels (start of every two quarters) without crowding.
     */
    labels?: string[]
    /**
     * Optional per-bucket labels rendered inside the hover tooltip. Same
     * length / index convention as `buckets` and `labels`. When omitted
     * the tooltip falls back to `labels[i]`. Splitting the two lets a
     * caller keep year context on the axis (e.g. `Mar '24`) while showing
     * a more compact form in the tooltip (`Mar`), which is the format the
     * client card uses since the axis already disambiguates the year.
     */
    tooltipLabels?: string[]
    /**
     * SVG `width` attribute. Defaults to `240` to match the mock and most
     * layouts inside the client info card. Numbers are interpreted as
     * pixels.
     */
    width?: number
    /**
     * SVG bar area `height` (excluding the axis label band). Defaults to
     * `36`, again matching the mock's compact sparkline.
     */
    height?: number
    /**
     * Bar fill color. Accepts any CSS color (named, hex, custom property
     * via `var(--…)`). Defaults to the app's positive money color so the
     * sparkline reads as a "lessons / activity" indicator.
     */
    color?: string
    /**
     * How frequently to render an axis label, expressed as a stride over
     * the `labels` array (e.g. `6` ⇒ every 6 buckets). Set to `0` or a
     * negative number to suppress labels entirely.
     */
    labelStride?: number
    /**
     * Optional accessible title rendered as the SVG `<title>` element.
     * When omitted no title is emitted; the SVG remains
     * `aria-hidden="true"` so screen readers skip the decorative chart.
     */
    title?: string
  }>(),
  {
    labels: () => [],
    tooltipLabels: () => [],
    width: 240,
    height: 36,
    color: 'var(--color-money-positive)',
    labelStride: 6,
    title: undefined,
  },
)

const topInset = 4
// Vertical buffer reserved above the bar area so the top y-axis tick
// label (drawn at `y=2` with `dominant-baseline="central"` to align
// with the tallest bar's top edge) is not clipped by the SVG viewport.
// Without this buffer the upper half of the ~7px tick glyph extends to
// roughly `y=-1.5`, which lies outside the viewBox and renders as a
// truncated digit. 4px is the smallest value that fully clears the
// glyph for our `7px` mono font without adding noticeable whitespace
// above the chart.

const totalHeight = computed(() => props.height + 10 + topInset)
// Reserves `topInset` above the bars (for the topmost y-axis label
// glyph) and a 10px band beneath the bars for the (optional) x-axis
// labels; keeps the bar geometry independent of label visibility. Bar
// y-coordinates are unaffected because the viewBox below shifts the
// origin upward by `topInset` instead of pushing the bars down.

const barCount = computed(() => Math.max(props.buckets.length, 1))
// Clamps to `1` so the math below never divides by zero when the parent
// passes an empty array (the chart still renders an empty axis).

const padding = 2
// Inner padding inside the SVG so the leftmost / rightmost bars don't
// touch the edges. Mirrors the mock; the value is small enough that a
// 240px chart still fits 24 bars comfortably.

const yAxisGutter = 16
// Width of the band on the left side of the chart reserved for the
// y-axis tick numbers (max at the top, `0` at the baseline). 16px fits
// up to a 3-digit integer in the 7px monospace label font without
// touching the leftmost bar. The bar geometry below shifts right by
// this amount so the bars themselves never overlap the ticks.

const chartLeft = computed(() => yAxisGutter)
// Pixel x of the left edge of the bar plotting area; everything that
// targets a "bar index" position composes this offset.

const chartInnerWidth = computed(() => props.width - yAxisGutter)
// Plotting width available to the bars after the y-axis gutter has been
// reserved. Uses `props.width` as the SVG viewport width so the chart
// still scales to its container.

const barWidth = computed(() => (chartInnerWidth.value - padding * 2) / barCount.value)

const maxValue = computed(() => {
  let m = 0
  for (const v of props.buckets) {
    if (typeof v === 'number' && Number.isFinite(v) && v > m) m = v
  }
  return m
})
// `0` when the series is empty or all-zero so the renderer falls back to
// drawing every bar at the muted "empty" opacity (see `bars` below).

interface BarShape {
  /** Visible bar geometry (top-anchored at the data value, baseline at the bar floor). */
  x: number
  y: number
  w: number
  h: number
  empty: boolean
  /**
   * Bucket value (lesson count) at this index. Cached on the bar so the
   * tooltip / aria-label code does not need to re-index the buckets prop
   * with the hovered idx.
   */
  value: number
  /** Optional label aligned with the bucket; mirrors `props.labels[i]`. */
  label: string | undefined
  /**
   * Per-bucket label preferred by the hover tooltip; falls back to
   * `label` when the caller has not provided a separate tooltip label
   * for this index. Cached on the bar (rather than re-indexed on every
   * tooltip render) so the tooltip's text is stable while the bar is
   * hovered, even if the parent reactively re-renders the labels arrays.
   */
  tooltipLabel: string | undefined
}

const bars = computed<BarShape[]>(() => {
  const result: BarShape[] = []
  const max = maxValue.value
  for (let i = 0; i < barCount.value; i++) {
    const v = props.buckets[i] ?? 0
    const empty = !(v > 0)
    const h = max > 0 ? (v / max) * (props.height - 4) : 0
    const colX = chartLeft.value + padding + i * barWidth.value
    result.push({
      x: colX + 0.5,
      y: props.height - h - 2,
      w: Math.max(barWidth.value - 1.5, 1),
      h,
      empty,
      value: v,
      label: props.labels[i],
      tooltipLabel: props.tooltipLabels[i] ?? props.labels[i],
    })
  }
  return result
})

interface AxisLabel {
  // One x-axis label rendered below the chart at a strided interval.

  x: number
  // SVG x coordinate (centre of the corresponding bar column).

  text: string
  // Label text from `props.labels[i]`.
}

const axisLabels = computed<AxisLabel[]>(() => {
  const stride = props.labelStride
  if (stride == null || stride <= 0) return []
  const result: AxisLabel[] = []
  for (let i = 0; i < props.labels.length; i += stride) {
    const text = props.labels[i]
    if (typeof text !== 'string' || text.length === 0) continue
    result.push({
      x: chartLeft.value + padding + i * barWidth.value + barWidth.value / 2,
      text,
    })
  }
  return result
})

interface YAxisTick {
  // One tick on the y-axis. `y` is the SVG vertical coordinate aligned with
  // the value the tick represents (top of the tallest bar for `max`, bar
  // baseline for `0`). `text` is rendered right-aligned at the gutter edge.
  y: number
  text: string
}

const yAxisTicks = computed<YAxisTick[]>(() => {
  /* No y-axis ticks when the series is all-zero: every bar renders at the
   * muted "empty" opacity, so adding a `max=0` tick would only repeat
   * information already implied by the chart's empty state. */
  const max = maxValue.value
  if (!(max > 0)) return []
  /* `bars` use the range [props.height - 2, 2] for [0, max]; the ticks
   * mirror those endpoints so the labels read against the actual bar
   * geometry rather than an abstract scale. */
  return [
    { y: 2, text: String(Math.round(max)) },
    { y: props.height - 2, text: '0' },
  ]
})

/*
 * Hover tooltip state.
 *
 * Native SVG `<title>` tooltips were tried first but proved unreliable —
 * Safari skips the popup on transparent fills and Chrome/Firefox impose a
 * ~1s hover delay that reads as broken to the user. We instead wire a
 * single `mousemove` listener on the chart wrapper, project the cursor x
 * into the SVG's viewBox, and resolve the closest bar index. This keeps
 * the per-bar SVG markup minimal and handles empty / zero-height bars
 * with no extra hit rects.
 */
const wrapperRef = ref<HTMLElement | null>(null)
const hoveredIndex = ref<number | null>(null)
const tooltipLeft = ref(0)
const tooltipTop = ref(0)

const hoveredBar = computed(() =>
  hoveredIndex.value === null ? null : (bars.value[hoveredIndex.value] ?? null),
)

const tooltipText = computed(() => {
  /* Compact "<label> <count>" form (e.g. "Nov 7"); the surrounding chart
   * caption already establishes that the count refers to lessons, so we
   * drop the noun and keep the tooltip narrow enough to sit between two
   * adjacent bars on a 240-px chart without overflowing the card. The
   * label is omitted when missing rather than padded with a separator.
   *
   * Prefers `tooltipLabel` (which the client card sets to a year-less
   * `Oct`) so the popup stays compact; falls back to the axis `label`
   * when the caller has not split the two streams. */
  const bar = hoveredBar.value
  if (!bar) return ''
  const lbl = bar.tooltipLabel ?? bar.label
  return lbl ? `${lbl} ${bar.value}` : String(bar.value)
})

function onMouseMove(event: MouseEvent) {
  const wrapper = wrapperRef.value
  if (!wrapper) return
  const rect = wrapper.getBoundingClientRect()
  if (rect.width <= 0) return
  /* Project the cursor's pixel x onto the SVG viewBox so the bar index
   * computation is independent of the chart's CSS-driven render width. */
  const localPx = event.clientX - rect.left
  const svgX = (localPx / rect.width) * props.width
  const innerX = svgX - chartLeft.value - padding
  if (innerX < 0 || innerX > chartInnerWidth.value - padding * 2) {
    hoveredIndex.value = null
    return
  }
  const idx = Math.min(barCount.value - 1, Math.max(0, Math.floor(innerX / barWidth.value)))
  hoveredIndex.value = idx
  /* Anchor the tooltip to the cursor with a small upward offset so it
   * never sits under the pointer (which would block re-entry events on
   * narrow bars). */
  tooltipLeft.value = localPx
  tooltipTop.value = event.clientY - rect.top
}

function onMouseLeave() {
  hoveredIndex.value = null
}
</script>

<template>
  <!--
    The wrapper hosts both the SVG and the absolutely-positioned tooltip
    overlay. `position: relative` on the wrapper anchors the tooltip
    coordinates to the chart's bounding box. A single `mousemove` listener
    here scales much better than per-bar listeners and works for empty
    months (where the visible <rect> would otherwise have h=0 and not
    receive pointer events).
  -->
  <div
    ref="wrapperRef"
    class="monthly-bar-chart-wrapper"
    @mousemove="onMouseMove"
    @mouseleave="onMouseLeave"
  >
    <svg
      class="monthly-bar-chart"
      :viewBox="`0 ${-topInset} ${width} ${totalHeight}`"
      :width="width"
      :height="totalHeight"
      :role="title ? 'img' : 'presentation'"
      :aria-label="title"
      :aria-hidden="title ? undefined : 'true'"
    >
      <title v-if="title">{{ title }}</title>
      <!-- Optional highlight band drawn behind the hovered column so the
           user gets visual feedback that the tooltip is tied to a specific
           month. Rendered first so it sits beneath the bar fill. -->
      <rect
        v-if="hoveredBar"
        :x="hoveredBar.x - 0.5"
        y="0"
        :width="hoveredBar.w + 1"
        :height="height"
        class="monthly-bar-chart__hover-band"
      />
      <rect
        v-for="(bar, idx) in bars"
        :key="idx"
        :x="bar.x"
        :y="bar.y"
        :width="bar.w"
        :height="bar.h"
        :fill="color"
        :opacity="bar.empty ? 0.08 : 0.8"
      />
      <!-- y-axis ticks (max + 0) sit in the left gutter; right-aligned at the
           gutter edge with `dominant-baseline="central"` so the numbers stay
           vertically anchored to the values they represent (top of the
           tallest bar / baseline). -->
      <text
        v-for="(tick, idx) in yAxisTicks"
        :key="`y-${idx}`"
        :x="yAxisGutter - 3"
        :y="tick.y"
        text-anchor="end"
        dominant-baseline="central"
        class="monthly-bar-chart__axis-label"
        fill="currentColor"
      >
        {{ tick.text }}
      </text>
      <text
        v-for="(label, idx) in axisLabels"
        :key="`label-${idx}`"
        :x="label.x"
        :y="height + 9"
        text-anchor="middle"
        class="monthly-bar-chart__axis-label"
        fill="currentColor"
      >
        {{ label.text }}
      </text>
    </svg>
    <div
      v-if="hoveredBar"
      class="monthly-bar-chart__tooltip"
      :style="{ left: `${tooltipLeft}px`, top: `${tooltipTop}px` }"
      role="tooltip"
    >
      {{ tooltipText }}
    </div>
  </div>
</template>

<style scoped>
.monthly-bar-chart-wrapper {
  position: relative;
  /* The wrapper takes the chart's intrinsic block size so the tooltip's
     `top` coordinates remain inside its bounding box; without this the
     wrapper would inline-shrink and the cursor-relative tooltip could
     escape the visual area on small screens. */
  display: block;
  width: 100%;
}

.monthly-bar-chart {
  display: block;
  /* Lets the SVG scale into its parent container while preserving its aspect
     ratio; matches how the mock embeds the sparkline beneath the section
     label. */
  width: 100%;
  max-width: 100%;
  height: auto;
  color: var(--color-tufte-muted);
}

.monthly-bar-chart__axis-label {
  font-family: var(--font-tufte-mono);
  font-size: 7px;
  /* Inherits color via `fill="currentColor"` so themes can recolor the axis
     by setting the parent's `color`. */
}

.monthly-bar-chart__hover-band {
  /* Subtle wash that lights up the hovered column; intentionally muted so
     it does not compete with the bar itself. `pointer-events: none` keeps
     the wrapper's mousemove handler authoritative for hit-testing. */
  fill: var(--color-tufte-muted);
  fill-opacity: 0.08;
  pointer-events: none;
}

.monthly-bar-chart__tooltip {
  /* Absolutely-positioned card anchored to the cursor with a small upward
     offset so the pointer never sits on top of the tooltip (which would
     otherwise block hover re-entry). `pointer-events: none` is critical:
     without it, the tooltip would intercept the mousemove on the wrapper
     and start flickering. Padding is intentionally tight (1px / 4px) so
     a short "<label> <count>" pair fits within the width of two adjacent
     bars on the typical 240-px chart. */
  position: absolute;
  transform: translate(-50%, calc(-100% - 6px));
  background: var(--color-tufte-bg);
  color: var(--color-tufte-fg);
  border: 1px solid var(--color-tufte-border);
  border-radius: 2px;
  padding: 1px 4px;
  font-family: var(--font-tufte-mono);
  font-size: 10px;
  line-height: 1.2;
  white-space: nowrap;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  pointer-events: none;
  z-index: 1;
}
</style>
